'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useAtomValue, useSetAtom } from 'jotai'
import { CheckIcon, ChevronRightIcon, ShieldAlertIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import {
  HOME_EASE,
  HOME_LIST,
  HOME_ROW,
  HOME_SPRING,
  HomeHeaderLink,
  HomeMeter,
  HomePanel,
} from '@/features/home/components/home-motion'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useStartWelcomeTour } from '@/features/onboarding/hooks/use-start-welcome-tour'
import { TutorialProgressRing } from '@/features/tutorial/components/tutorial-progress-ring'
import { useOpenTutorialHub } from '@/features/tutorial/hooks/use-open-tutorial-hub'
import { useTutorialCompletion } from '@/features/tutorial/hooks/use-tutorial-completion'
import { tutorialState } from '@/features/tutorial/lib/tutorial-progress'
import type { TutorialId } from '@/features/tutorial/lib/tutorials'
import { cn } from '@/lib/utils'
import { welcomeGuideAtom } from '@/state/onboarding'
import { tutorialProgressAtom, tutorialRequestAtom } from '@/state/tutorial'

type StepId = 'connect' | 'tour' | 'aiOrder' | 'firstTrade' | 'stressTest' | 'hedge'

// Trading safely, one line at a time: the mistakes that cost newcomers money first.
const SAFETY_TIPS = ['leverage', 'stopLoss', 'smallSize', 'liquidation'] as const
const TIP_INTERVAL_MS = 8000

interface HomeGettingStartedProps {
  snapshot: HomeSnapshot
  onConnectExchange: () => void
  onOpenTrading: () => void
  className?: string
}

/**
 * Onboarding as a checklist of real milestones rather than a walkthrough to skip: each step is
 * ticked off by what the user actually did (an account connected, an order placed, a tutorial
 * finished) and opens the thing it is about. Below it, a rotating "trade safely" tip — this is
 * money, so the basics of leverage and liquidation are always one glance away.
 */
export function HomeGettingStarted({
  snapshot,
  onConnectExchange,
  onOpenTrading,
  className,
}: HomeGettingStartedProps) {
  const t = useTranslations('trade.home.gettingStarted')
  const progress = useAtomValue(tutorialProgressAtom)
  const welcome = useAtomValue(welcomeGuideAtom)
  const setTutorialRequest = useSetAtom(tutorialRequestAtom)
  const startWelcomeTour = useStartWelcomeTour()
  const openTutorialHub = useOpenTutorialHub()
  const completion = useTutorialCompletion()
  const [tipIndex, setTipIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(
      () => setTipIndex((index) => (index + 1) % SAFETY_TIPS.length),
      TIP_INTERVAL_MS
    )
    return () => clearInterval(timer)
  }, [tipIndex])

  const tutorialDone = (id: TutorialId) => tutorialState(progress, id).status === 'done'
  const startTutorial = (id: TutorialId) => setTutorialRequest({ id, source: 'hub' })

  const steps: { id: StepId; done: boolean; run: () => void }[] = [
    { id: 'connect', done: snapshot.portfolio.accountCount > 0, run: onConnectExchange },
    { id: 'tour', done: welcome.dismissed, run: startWelcomeTour },
    {
      id: 'aiOrder',
      done: tutorialDone('chat-order-form'),
      run: () => startTutorial('chat-order-form'),
    },
    { id: 'firstTrade', done: snapshot.activity.length > 0, run: onOpenTrading },
    {
      id: 'stressTest',
      done: tutorialDone('risk-slides-page'),
      run: () => startTutorial('risk-slides-page'),
    },
    { id: 'hedge', done: tutorialDone('hedge'), run: () => startTutorial('hedge') },
  ]
  const doneCount = steps.filter((step) => step.done).length
  const allDone = doneCount === steps.length
  const nextStep = steps.find((step) => !step.done)
  const tip = SAFETY_TIPS[tipIndex]

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <HomeHeaderLink onClick={() => openTutorialHub()}>
          <TutorialProgressRing
            done={completion.done}
            total={completion.total}
            className="text-shell-accent size-3.5"
          />
          {t('learningHub')}
        </HomeHeaderLink>
      }
    >
      <div className="mb-3 flex items-center gap-3">
        <HomeMeter
          value={doneCount / steps.length}
          className="flex-1"
          barClassName="bg-shell-accent"
        />
        <span className="text-foreground/50 text-xs whitespace-nowrap">
          {t('progress', { done: doneCount, total: steps.length })}
        </span>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {allDone ? (
          <motion.div
            key="all-done"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={HOME_SPRING}
            className="flex flex-1 flex-col items-start justify-center gap-1 py-4"
          >
            <p className="font-display text-foreground text-xl tracking-tight">
              {t('allDoneTitle')}
            </p>
            <p className="text-foreground/55 text-sm">{t('allDoneHint')}</p>
          </motion.div>
        ) : (
          <motion.ul
            key="steps"
            variants={HOME_LIST}
            initial="hidden"
            animate="show"
            className="flex flex-col"
          >
            {steps.map((step) => {
              const isNext = step.id === nextStep?.id
              return (
                <motion.li key={step.id} variants={HOME_ROW}>
                  <button
                    type="button"
                    onClick={step.run}
                    className={cn(
                      'group hover:bg-foreground/5 relative flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors',
                      isNext && 'bg-foreground/[0.04]'
                    )}
                  >
                    <span
                      className={cn(
                        'relative flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
                        step.done
                          ? 'bg-shell-accent border-shell-accent text-background'
                          : 'border-foreground/25 group-hover:border-foreground/50'
                      )}
                    >
                      <AnimatePresence initial={false}>
                        {step.done && (
                          <motion.span
                            initial={{ scale: 0, rotate: -45 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0 }}
                            transition={{ type: 'spring', stiffness: 600, damping: 22 }}
                            className="flex"
                          >
                            <CheckIcon className="size-3" strokeWidth={3} />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span
                        className={cn(
                          'truncate text-sm tracking-tight transition-colors',
                          step.done ? 'text-foreground/40 line-through' : 'text-foreground'
                        )}
                      >
                        {t(`steps.${step.id}.title`)}
                      </span>
                      {isNext && (
                        <span className="text-foreground/50 truncate text-xs">
                          {t(`steps.${step.id}.hint`)}
                        </span>
                      )}
                    </span>
                    {!step.done && (
                      <ChevronRightIcon className="text-foreground/30 group-hover:text-foreground size-4 shrink-0 transition-[color,translate] duration-200 group-hover:translate-x-0.5" />
                    )}
                  </button>
                </motion.li>
              )
            })}
          </motion.ul>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setTipIndex((index) => (index + 1) % SAFETY_TIPS.length)}
        className="border-foreground/10 bg-ox-gold/[0.06] mt-3 flex cursor-pointer items-start gap-2.5 overflow-hidden rounded-md border px-3 py-2.5 text-left"
        aria-label={t('safety.next')}
      >
        <ShieldAlertIcon className="text-ox-gold mt-0.5 size-4 shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-ox-gold text-[11px] font-medium tracking-wide uppercase">
            {t('safety.title')}
          </span>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={tip}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.24, ease: HOME_EASE }}
              className="text-foreground/75 text-xs leading-5"
            >
              {t(`safety.tips.${tip}`)}
            </motion.span>
          </AnimatePresence>
        </span>
        <span className="mt-1 flex shrink-0 gap-1" aria-hidden="true">
          {SAFETY_TIPS.map((id) => (
            <span
              key={id}
              className={cn(
                'size-1 rounded-full transition-colors duration-300',
                id === tip ? 'bg-ox-gold' : 'bg-foreground/20'
              )}
            />
          ))}
        </span>
      </button>
    </HomePanel>
  )
}
